import { Subject } from 'rxjs';

import {
    ConvertOption,
    ConvertOptionCategories,
    ConvertOptionCategoy,
    DropdownWidget,
    InputDropdownWidget,
    InputWidget,
    WidgetTypes,
} from '../../types/dropdown.type';
import { BrowserUtils } from '../../ui-utils/browser-utils';

export interface HTMLWidgetChangeEvent {
    formChild?: any;
    selectedValue?: any;
}

export interface ConvertOptionChangeEvent {
    selection?: any;
    group?: ConvertOption;
}

export class ConvertOptionsContext {
    private selectedOptionsChanged = new Subject<HTMLWidgetChangeEvent & ConvertOptionChangeEvent>();
    private convertOptionCategories: ConvertOptionCategories = null;

    private constructor(
        convertOptionCategories: ConvertOptionCategories,
    ) {
        this.convertOptionCategories = convertOptionCategories;
      BrowserUtils.runIfWindowExists(() => {
        ( window as any).context = this
      })
    }

    get onSelectedOptionsChanged() {
        return this.selectedOptionsChanged.asObservable();
    }

    static newContext(convertOptionCategories: ConvertOptionCategories) {
        // TODO If changed to SPA, then use cloneDeep(convertOptionCategories)
        return new ConvertOptionsContext(convertOptionCategories);
    }

    emitEvent(event: any) {
        this.selectedOptionsChanged.next(event)
    }

    destroy() {
        this.selectedOptionsChanged.complete();
    }

    getConvertOptionCategories() {
        return this.convertOptionCategories;
    }

    private convertOptionHasSelectedValue(convertOption: ConvertOption): boolean {
        return convertOption.selections && !!convertOption.selections.find(selection => selection.selected);
    }

    private getConvertOptionSelectedValue(convertOption: ConvertOption) {
        return convertOption.selections.find(selection => selection.selected);
    }

    private isLeaf(convertOption: ConvertOption) {
        if (!this.convertOptionHasSelectedValue(convertOption)) {
            return false;
        }
        return this.getConvertOptionSelectedValue(convertOption).displayAs !== 'parent';
    }

    private isParent(convertOption: ConvertOption) {
        if (!this.convertOptionHasSelectedValue(convertOption)) {
            return false;
        }
        const selectedValue = this.getConvertOptionSelectedValue(convertOption);
        return selectedValue && selectedValue.displayAs === 'parent';
    }

    private isDropdown(convertOption: ConvertOption) {
        if (!this.convertOptionHasSelectedValue(convertOption)) {
            return false;
        }
        const selectedValue = this.getConvertOptionSelectedValue(convertOption);
        return !selectedValue.displayAs || selectedValue.displayAs === 'leaf'
            || selectedValue.displayAs === DropdownWidget.TYPE;
    }

    private isInputDropdown(convertOption: ConvertOption) {
        const selectedValue: any = convertOption;
        return selectedValue && (selectedValue.displayAs
            && selectedValue.displayAs === InputDropdownWidget.TYPE);
    }

    private isInput(convertOption: ConvertOption) {
        const selectedValue: any = convertOption;
        return selectedValue && (selectedValue.displayAs
            && selectedValue.displayAs === InputWidget.TYPE);
    }

    private isSeekbar(convertOption: ConvertOption) {
        if (!this.convertOptionHasSelectedValue(convertOption)) {
            return false;
        }
        const selectedValue = this.getConvertOptionSelectedValue(convertOption);
        return !selectedValue.displayAs
            && (selectedValue.displayAs as any) === WidgetTypes.DROPDOWN;
    }

    private isCheckbox(convertOption: ConvertOption) {
        if(convertOption && (convertOption as any).displayAs=== WidgetTypes.CHECKBOX) {
            return true;
        }
        return false;
    }

    private getDropdownSelectedValue(convertOption: ConvertOption) {
        const selectedValue = this.getConvertOptionSelectedValue(convertOption);
        return selectedValue && selectedValue.formValue;
    }

    private getInputDropdownSelectedValue(convertOption: ConvertOption) {
        const selectedValue: any = convertOption;
        return selectedValue && selectedValue.formValue && selectedValue.formValue.input;
    }

    private getInputDropdownSelectedType(convertOption: ConvertOption) {
        const selectedValue: any = convertOption;
        return selectedValue && selectedValue.formValue && selectedValue.formValue.dropdown && selectedValue.formValue.dropdown.formValue;
    }

    private getInputSelectedValue(convertOption: ConvertOption) {
        const selectedValue: any = convertOption;
        return selectedValue && selectedValue.formValue;
    }

    private getCheckboxValue(convertOption: ConvertOption) {
        const selectedValue: any = convertOption;
        return !!(selectedValue && selectedValue.formValue);
    }

    summarize(convertOption: ConvertOption, summary) {
        if (!this.isParent(convertOption)) {
            if(this.isCheckbox(convertOption)) {
                summary.push({
                    formName: convertOption.formGroupName || (convertOption as any).formName,
                    formValue: this.getCheckboxValue(convertOption),
                });
            } else if (this.isDropdown(convertOption)) {
                summary.push({
                    formName: convertOption.formGroupName || (convertOption as any).formName,
                    formValue: this.getDropdownSelectedValue(convertOption),
                });
            } else if (this.isInputDropdown(convertOption)) {
                summary.push({
                    formName: convertOption.formGroupName || (convertOption as any).formName,
                    formValue: this.getInputDropdownSelectedValue(convertOption),
                    formValueType: this.getInputDropdownSelectedType(convertOption),
                });
            } else if (this.isInput(convertOption)) {
                summary.push({
                    formName: convertOption.formGroupName || (convertOption as any).formName,
                    formValue: this.getInputSelectedValue(convertOption),
                });
            } else {
                console.error('Unknown convert option type');
            }
        } else {
            const selectedItems = convertOption.selections.filter(item => item.selected);
            selectedItems.forEach(item => {
                const subItemSummary = [];
                item.formChildren.map(subItem => this.summarize(subItem, subItemSummary))
                summary.push({
                    formName: convertOption.formGroupName,
                    formValue: subItemSummary,
                });
            })
        }
    }

    toConvertOptionsSummary() {
        const summary = [];
        const categories = this.getConvertOptionCategories()
            .formCategories
            .filter(category => !(category.hiddenFn && category.hiddenFn(this)));

        categories.forEach((category: ConvertOptionCategoy) => {
            category.formGroups.forEach(convertOption => {
                this.summarize(convertOption, summary);
            });
        });
        return summary;
    }
}